import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  FullPhysicianResponse,
  PatientFacingPhysicianResponse,
  PermissionBit,
} from "../../api";
import { ApiConnector } from "../../api/api-connector";
import { AppModel } from "../../app/app-model";
import { errorText } from "../../app/error-text";
import { PageHeaderActions } from "../../app/layout/layout-context";
import { location } from "../../app/location";
import { useCountries } from "../../catalogs/countries";
import { AssignedOnlyToggle } from "../../components/assigned-only-toggle";
import { MonitoringUserBubbles } from "../../components/monitoring-users/monitoring-user-bubbles";
import { monitoringUserFilter } from "../../components/monitoring-users/monitoring-user-filter";
import { useMonitoringAssignments } from "../../components/monitoring-users/use-monitoring-assignments";
import { QuickLookPerLevel } from "../../components/quick-look/quick-look-button";
import {
  Callout,
  CalloutDescription,
} from "../../components/ui-kit/base/callout";
import { DialogClose } from "../../components/ui-kit/base/dialog";
import { HelperTooltip } from "../../components/ui-kit/base/helper-tooltip";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui-kit/base/tabs";
import { Button } from "../../components/ui-kit/button";
import { useDialog } from "../../components/ui-kit/dialog-provider";
import { Spinner } from "../../components/ui-kit/spinner";
import { CellEnum } from "../../components/ui-kit/tables/cells/cell-enum";
import { CellOverflowList } from "../../components/ui-kit/tables/cells/cell-overflow-list";
import {
  type Column,
  DataTable,
} from "../../components/ui-kit/tables/data-table";
import {
  monitoringTypes,
  patientStates,
  patientStatuses,
} from "../../constants/enum-to-text";
import {
  hasMonitoringAccess,
  hasPermission,
} from "../../constants/permissions";
import { consentOk } from "../../utils/data-utils";
import {
  deviceConfigFilterOptions,
  DeviceConfigIcons,
  deviceConfigPredicate,
  renderDeviceConfigOption,
} from "../../utils/device-config-filter";
import {
  getCountryOptions,
  getPhysicianOptions,
  getRegionOptions,
  physicianPredicate,
  regionDisabled,
  regionPredicate,
} from "../../utils/filter-presets";
import { getAge } from "../../utils/utils";
import { LegacyPatientSearch } from "./legacy-patient-search";
import { patientPath } from "./patient-path";
import { FullPatientsItem } from "./patients-tab-model";

export const PatientsRoute = observer(function PatientsRoute({
  api,
  app,
}: {
  api: ApiConnector;
  app: AppModel;
}) {
  const { t, i18n } = useLingui();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openDialog } = useDialog();
  const countries = useCountries();
  const monitoringAssignments = useMonitoringAssignments(api);

  useEffect(() => {
    // Read `?tab=` from the live URL so we don't re-init on every URL change.
    const initialTab =
      new URL(location.href).searchParams.get("tab") ?? undefined;
    void app.initPatientsTabs(initialTab);
  }, [app]);

  useEffect(() => {
    const createdPatientId = searchParams.get("createdPatientId");
    if (!createdPatientId) return;
    // Preserve `?tab=` (and anything else) while clearing the one-shot param.
    const next = new URL(location.href).searchParams;
    next.delete("createdPatientId");
    setSearchParams(next, { replace: true });
    void openDialog({
      title: t`Patient created`,
      body: (
        <p className="text-sm text-muted-foreground">
          <Trans>
            Patient <strong>{createdPatientId}</strong> was successfully
            created.
          </Trans>
        </p>
      ),
      footer: (
        <DialogClose asChild>
          <Button variant="primary">
            <Trans>OK</Trans>
          </Button>
        </DialogClose>
      ),
    });
  }, [searchParams, setSearchParams, openDialog, t]);

  const tabs = app.patientsTabs;

  if (!tabs) {
    return <Spinner />;
  }

  const activeModel = tabs.activeTab;

  // Filters are grouped by string equality, so each group name must be
  // defined exactly once.
  const filterGroups = {
    identity: t`Identity`,
    careTeam: t`Care team`,
    clinical: t`Clinical`,
    location: t`Location`,
    status: t`Status`,
  };

  const screening = activeModel?.screening ?? false;
  // A screening patient's name, demographics and physician are placeholders
  // ("Screening Account"); only the screening user's login tells them apart.
  const identityColumns: Column<FullPatientsItem>[] = screening
    ? [
        {
          header: <Trans>Username</Trans>,
          attr: "screening_username",
          type: "string",
          width: 195,
          filter: {
            kind: "text",
            group: filterGroups.identity,
            placeholder: t`Search…`,
          },
        },
        {
          header: <Trans>Email</Trans>,
          attr: "screening_email",
          type: "string",
          width: 195,
          filter: {
            kind: "text",
            group: filterGroups.identity,
            placeholder: t`Search…`,
          },
        },
      ]
    : [
        {
          header: <Trans>Name</Trans>,
          attr: "full_name",
          type: "string",
          width: 195,
          filter: {
            kind: "text",
            group: filterGroups.identity,
            placeholder: t`Search…`,
          },
        },
        {
          header: <Trans>Age / Gender</Trans>,
          width: 50,
          cell: ({ item }) => {
            const age = item.date_of_birth ? getAge(item.date_of_birth) : "–";
            const sex = item.sex ?? "–";
            return (
              <span>
                {age} {sex}
              </span>
            );
          },
        },
        {
          header: <Trans>Physician(s)</Trans>,
          attr: "physicians",
          width: 200,
          filter: {
            kind: "multi",
            group: filterGroups.careTeam,
            options: getPhysicianOptions,
            predicate: physicianPredicate,
          },
          cell: ({ item }) => {
            const physicians: Array<
              PatientFacingPhysicianResponse | FullPhysicianResponse
            > = item.physicians ?? [];
            if (!physicians.length) return "";
            return (
              <CellOverflowList
                maxInline={1}
                items={physicians.map((p) => (
                  <span
                    key={p.uuid}
                    className="inline-flex items-center gap-1.5 align-middle"
                  >
                    {p.full_name}
                    <MonitoringUserBubbles
                      users={monitoringAssignments.forPhysician(p.uuid)}
                    />
                  </span>
                ))}
              />
            );
          },
        },
      ];

  const allColumns: Column<FullPatientsItem>[] = [
    // A trend report (HeartCore / DRI) doesn't apply to a screening bundle.
    ...(screening
      ? []
      : [
          {
            header: <Trans>Patient report</Trans>,
            requiredPermission: PermissionBit.RUN_HEART_CORE_QUICKLOOK,
            width: 100,
            cell: ({ item }: { item: FullPatientsItem }) => (
              <QuickLookPerLevel api={api} patientId={item.patient_id} />
            ),
          },
        ]),
    {
      header: (
        <span className="flex items-center gap-1">
          <Trans>Patient HF study ID</Trans>
          <HelperTooltip
            content={
              <Trans>
                Patients who have not given informed consent are marked here.
                Users without permission to view them see their IDs greyed out
                and cannot open their profiles.
              </Trans>
            }
          />
        </span>
      ),
      attr: "patient_id",
      filter: {
        kind: "text",
        label: t`Patient HF study ID`,
        group: filterGroups.identity,
        placeholder: t`Search…`,
      },
      width: 100,
      cell: ({ item }) => {
        const notConsented = !consentOk(item);
        const disabled =
          notConsented &&
          !hasPermission(api.permissions, PermissionBit.BYPASS_CONSENT);

        return (
          <span className="inline-flex items-center gap-1">
            <Link
              className={clsx(
                "font-bold",
                disabled &&
                  "text-muted-foreground no-underline pointer-events-none",
              )}
              tabIndex={disabled ? -1 : undefined}
              aria-disabled={disabled}
              to={!disabled ? patientPath(item) : "#"}
            >
              {item.patient_id}
            </Link>
            {notConsented && (
              <HelperTooltip
                content={
                  <Trans>This patient has not given informed consent.</Trans>
                }
              />
            )}
          </span>
        );
      },
    },
    ...identityColumns,
    {
      header: <Trans>Monitoring user</Trans>,
      // The attr only provides a unique column id (it can't collide with the
      // visible "physicians" column) — the filter's predicate ignores it.
      attr: "uuid",
      hidden: true,
      requiredPermission: PermissionBit.GROUP_MONITORING_ROUTERS,
      filter: monitoringUserFilter({
        physiciansOf: (item) => item.physicians ?? [],
        resolve: (physician) =>
          monitoringAssignments.forPhysician(physician.uuid),
        kindOf: (uuid) => monitoringAssignments.findUser(uuid)?.kind,
      }),
    },
    {
      header: <Trans>Risk flag</Trans>,
      attr: "patient_state",
      type: "enum",
      values: patientStates,
      editable: true,
      requiredPermission: PermissionBit.GROUP_MONITORING_ROUTERS,
      filter: {
        kind: "multi",
        group: filterGroups.clinical,
        options: new Map(Object.entries(patientStates)),
      },
      width: 70,
    },
    {
      header: <Trans>Clinical risk score</Trans>,
      attr: "clinical_risk_score",
      width: 70,
    },
    {
      header: <Trans>Device config</Trans>,
      attr: "self_measurement_config",
      filter: {
        kind: "multi",
        group: filterGroups.clinical,
        options: deviceConfigFilterOptions,
        renderOption: renderDeviceConfigOption,
        predicate: deviceConfigPredicate,
      },
      width: 120,
      cell: ({ item }) => (
        <DeviceConfigIcons config={item.self_measurement_config} />
      ),
    },
    {
      header: <Trans>Region</Trans>,
      attr: "region",
      editable: true,
      filter: {
        kind: "multi",
        group: filterGroups.location,
        options: (table) => getRegionOptions(table, countries),
        disabled: regionDisabled,
        disabledMessage: t`Select a country first`,
        predicate: regionPredicate,
      },
      width: 120,
      cell: (props) => (
        <CellEnum
          {...props}
          attr="region"
          values={countries.getRegionsEnum(props.item.country)}
        />
      ),
    },
    {
      header: <Trans>Country</Trans>,
      attr: "country",
      type: "string",
      filter: {
        kind: "multi",
        group: filterGroups.location,
        options: getCountryOptions,
      },
    },
    {
      header: <Trans>Monitoring type</Trans>,
      attr: "monitoring_type",
      type: "enum",
      values: monitoringTypes,
      filter: {
        kind: "multi",
        group: filterGroups.status,
        options: new Map(Object.entries(monitoringTypes)),
      },
    },
    {
      header: <Trans>Status</Trans>,
      attr: "patient_status",
      type: "enum",
      values: patientStatuses,
      filter: {
        kind: "multi",
        group: filterGroups.status,
        options: new Map(Object.entries(patientStatuses)),
      },
      width: 100,
    },
    {
      header: <Trans>Added</Trans>,
      attr: "created_at",
      type: "datetime",
      sort: true,
    },
  ];

  // Clinical-monitoring columns mean nothing for a screening bundle, and its
  // monitoring type is always Screening.
  const screeningOmitted = new Set<keyof FullPatientsItem>([
    "patient_state",
    "clinical_risk_score",
    "self_measurement_config",
    "monitoring_type",
  ]);
  const columns = screening
    ? allColumns.filter((c) => !c.attr || !screeningOmitted.has(c.attr))
    : allColumns;

  const tabsList = (
    <TabsList className="w-auto border-b-0">
      {tabs.visibleDefs.map((def) => (
        <TabsTrigger key={def.id} value={def.id} className="flex-none">
          {i18n._(def.label)}
        </TabsTrigger>
      ))}
    </TabsList>
  );

  return (
    <>
      <PageHeaderActions>
        {hasMonitoringAccess(api.permissions) && (
          <AssignedOnlyToggle
            value={tabs.assignedOnly}
            onChange={(next) => {
              void tabs.setAssignedOnly(next);
            }}
          />
        )}
        {/* The new layout's search lives in the top bar; the legacy one keeps
            it here, beside the title. */}
        {app.legacyLayout && <LegacyPatientSearch model={app.patientSearch} />}
      </PageHeaderActions>
      <Tabs
        value={tabs.activeTabId}
        onValueChange={(id: string) => {
          void tabs.setTab(id);
          // Mirror the active tab into the URL so refresh restores it. Reset
          // page/pageSize because they belong to whichever tab is active —
          // the table remounts via `key={tabs.activeTabId}` and re-reads URL.
          const next = new URL(location.href).searchParams;
          if (id === tabs.visibleDefs[0]?.id) next.delete("tab");
          else next.set("tab", id);
          next.delete("page");
          next.delete("pageSize");
          setSearchParams(next, { replace: true });
        }}
      >
        {activeModel?.screeningUsersError !== undefined && (
          <Callout severity="warning" className="mb-3">
            <CalloutDescription>
              <Trans>
                Couldn't load the screening users, so usernames and emails are
                missing: {errorText(activeModel.screeningUsersError)}
              </Trans>
            </CalloutDescription>
          </Callout>
        )}
        {activeModel && (
          <DataTable
            key={tabs.activeTabId}
            persistState
            persistStateKey={tabs.activeTabId}
            model={activeModel}
            columns={columns}
            // Reading the observable map here (observer component) also
            // rebuilds the column defs when assignments load, refreshing
            // bubble cells and filter options.
            filterDeps={[monitoringAssignments.byPhysician]}
            filterNoun={(count) =>
              t`${plural(count, { one: "patient", other: "patients" })}`
            }
            filterActions={tabsList}
            isLoading={activeModel.loading}
            editMode="cell"
            itemKeyField="patient_id"
            onEdit={async (editedField, item) => {
              await activeModel.updatePatient(item.patient_id, editedField);
            }}
          />
        )}
      </Tabs>
    </>
  );
});
